/**
 * CopingCard — Serene Shell antd theme (ConfigProvider).
 *
 * Single machine-readable mapping of the Serene Shell palette onto Ant Design v5
 * tokens. Use for the admin portal (`packages/portal`) and any antd usage on the
 * Gatsby website. Replaces the legacy blue-triad `ccTheme` in
 * `packages/portal/src/styles/theme.ts`.
 *
 * Usage:
 *   import { ConfigProvider } from 'antd';
 *   import { sereneShellTheme } from './serene-shell.theme';
 *   <ConfigProvider theme={sereneShellTheme}> … </ConfigProvider>
 *
 * Heading font: antd has no separate heading-font token, so pair this with the
 * `headingFontCss` snippet below (or load serene-shell.css) so h1–h5 / .ant-typography
 * render in Plus Jakarta Sans while body text uses Lexend.
 *
 * Palette values mirror serene-shell.css and packages/mobile/src/design-system/.
 * Keep the three in sync (a future codegen step can derive all from one JSON).
 */
import type { ThemeConfig } from 'antd';

export const palette = {
  primary: '#4A654E',
  primaryHover: '#3c5440',
  primaryContainer: '#8BA88E',
  primaryFixed: '#CCEACE',
  onPrimary: '#ffffff',
  secondary: '#7D562D',
  secondaryContainer: '#FFCA98',
  secondaryFixed: '#FFDCBD',
  tertiary: '#5C6145',
  error: '#BA1A1A',
  surface: '#fdf9f5',
  surfaceLow: '#F7F3EF',
  surfaceContainer: '#F1EDE9',
  surfaceHigh: '#EBE7E4',
  white: '#ffffff',
  onSurface: '#1C1C19',
  onSurfaceVar: '#424842',
  outline: '#737972',
  outlineVar: '#C2C8C0',
} as const;

const fontBody = "'Lexend', system-ui, sans-serif";

export const sereneShellTheme: ThemeConfig = {
  cssVar: true,
  hashed: false,
  token: {
    // brand / status
    colorPrimary: palette.primary,
    colorInfo: palette.primary,
    colorSuccess: palette.primary,
    colorWarning: palette.secondary,
    colorError: palette.error,
    colorLink: palette.primary,
    colorLinkHover: palette.primaryHover,

    // text & surfaces
    colorTextBase: palette.onSurface,
    colorText: palette.onSurface,
    colorTextSecondary: palette.onSurfaceVar,
    colorTextTertiary: palette.outline,
    colorBgBase: palette.surface,
    colorBgLayout: palette.surface,
    colorBgContainer: palette.white,
    colorBgElevated: palette.white,
    colorBorder: palette.outlineVar,
    colorBorderSecondary: '#E3E0DB',

    // shape
    borderRadius: 16,
    borderRadiusLG: 24,
    borderRadiusSM: 8,

    // type
    fontFamily: fontBody,
    fontSize: 15,
    lineHeight: 1.6,

    // controls
    controlHeight: 40,
    controlHeightLG: 48,
    controlHeightSM: 32,

    // soft, warm elevation
    boxShadow: '0 8px 24px -14px rgba(28,28,25,0.18)',
    boxShadowSecondary: '0 18px 50px -20px rgba(74,101,78,0.28)',

    wireframe: false,
  },
  components: {
    Button: {
      borderRadius: 999,
      borderRadiusLG: 999,
      borderRadiusSM: 999,
      controlHeight: 44,
      controlHeightLG: 52,
      fontWeight: 600,
      paddingInline: 24,
      primaryShadow: 'none',
      defaultShadow: 'none',
      defaultBorderColor: palette.outlineVar,
      defaultColor: palette.onSurface,
    },
    Card: {
      borderRadiusLG: 24,
      colorBgContainer: palette.surfaceContainer,
      colorBorderSecondary: palette.outlineVar,
      paddingLG: 24,
    },
    Input: {
      borderRadius: 16,
      colorBgContainer: palette.surfaceLow,
      activeBorderColor: palette.primary,
      hoverBorderColor: palette.primaryContainer,
      activeShadow: '0 0 0 3px rgba(74,101,78,0.12)',
      controlHeight: 44,
    },
    InputNumber: { borderRadius: 16, colorBgContainer: palette.surfaceLow, controlHeight: 44 },
    Select: {
      borderRadius: 16,
      colorBgContainer: palette.surfaceLow,
      optionSelectedBg: palette.primaryFixed,
      optionSelectedColor: palette.onSurface,
      controlHeight: 44,
    },
    DatePicker: { borderRadius: 16, colorBgContainer: palette.surfaceLow, controlHeight: 44 },
    Modal: { borderRadiusLG: 32, contentBg: palette.white, headerBg: palette.white },
    Tag: { borderRadiusSM: 999, defaultBg: palette.primaryFixed, defaultColor: '#07200f' },
    Tabs: {
      inkBarColor: palette.primary,
      itemSelectedColor: palette.primary,
      itemColor: palette.onSurfaceVar,
      itemHoverColor: palette.primary,
      titleFontSize: 15,
    },
    Segmented: {
      itemSelectedBg: palette.primary,
      itemSelectedColor: palette.onPrimary,
      trackBg: palette.surfaceContainer,
      borderRadius: 999,
      borderRadiusSM: 999,
    },
    Table: {
      headerBg: palette.surfaceLow,
      headerColor: palette.onSurfaceVar,
      borderColor: palette.outlineVar,
      rowHoverBg: palette.surfaceLow,
      borderRadius: 16,
    },
    Menu: {
      // sage sider (matches app navigation chrome)
      itemBg: 'transparent',
      subMenuItemBg: 'transparent',
      itemColor: 'rgba(255,255,255,0.82)',
      itemHoverColor: palette.white,
      itemHoverBg: 'rgba(255,255,255,0.12)',
      itemSelectedColor: palette.white,
      itemSelectedBg: 'rgba(255,255,255,0.18)',
      itemBorderRadius: 12,
      activeBarBorderWidth: 0,
    },
    Layout: {
      siderBg: palette.primary,
      triggerBg: palette.primaryHover,
      headerBg: palette.white,
      bodyBg: palette.surface,
      headerHeight: 64,
    },
    Form: { labelColor: palette.onSurface, labelFontSize: 14 },
    Switch: { colorPrimary: palette.primary },
    Radio: { colorPrimary: palette.primary, borderRadius: 8 },
    Checkbox: { colorPrimary: palette.primary, borderRadius: 6 },
    Slider: { trackBg: palette.primaryContainer, trackHoverBg: palette.primary, handleColor: palette.primary },
    Progress: { defaultColor: palette.primary },
    Alert: { borderRadiusLG: 16 },
    Tooltip: { borderRadius: 12, colorBgSpotlight: palette.onSurface },
    Notification: { borderRadiusLG: 24 },
    Message: { borderRadiusLG: 999 },
  },
};

/**
 * Companion CSS so antd headings use Plus Jakarta Sans (load Plus Jakarta Sans
 * + Lexend via Google Fonts). Inject globally (e.g. createGlobalStyle / index.css).
 */
export const headingFontCss = `
:root { --ss-font-heading: 'Plus Jakarta Sans', system-ui, sans-serif; }
h1, h2, h3, h4, h5,
.ant-typography h1, .ant-typography h2, .ant-typography h3, .ant-typography h4, .ant-typography h5,
h1.ant-typography, h2.ant-typography, h3.ant-typography, h4.ant-typography, h5.ant-typography,
.ant-page-header-heading-title, .ant-card-head-title, .ant-modal-title {
  font-family: var(--ss-font-heading);
  letter-spacing: -0.02em;
  font-weight: 700;
}
`;

export default sereneShellTheme;
